import { usePushNotifications } from "@/hooks/usePushNotifications";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useState } from "react";
import { Button } from "./ui/button";
import { BellIcon } from "lucide-react";
import { useStandaloneMode } from "@/hooks/useStandaloneMode";
import { useTranslation } from "@/hooks/useTranslation";
import { PageType } from "./PageType";

export default function EnableNotificationsDialog({
  pageType,
}: {
  pageType: PageType;
}) {
  const isStandalone = useStandaloneMode();
  const {
    isInitialized,
    isSupported,
    permission,
    error,
    requestNotificationPermission,
  } = usePushNotifications();
  const [open, setOpen] = useState(true);
  const [dismissCount, setDismissCount] = useState(() => {
    const storedCount = localStorage.getItem("notificationDismissCount");
    return storedCount ? parseInt(storedCount, 10) : 0;
  });
  const t = useTranslation();

  if (
    !isInitialized ||
    !isStandalone ||
    !isSupported ||
    permission !== "default" ||
    dismissCount >= 2
  ) {
    return;
  }

  const handleEnableNotifications = () => {
    requestNotificationPermission().catch((err) => {
      console.error("Failed to enable notifications:", err);
    });
  };

  const handleDismiss = () => {
    const newCount = dismissCount + 1;
    setDismissCount(newCount);
    localStorage.setItem("notificationDismissCount", newCount.toString());
    setOpen(false);
  };

  const dismissButtonText =
    dismissCount >= 1
      ? t.NOTIFICATIONS_DIALOG.LABELS.DONT_SHOW_BUTTON
      : t.NOTIFICATIONS_DIALOG.LABELS.DISMISS_BUTTON;

  return (
    <Dialog open={open} onOpenChange={() => setOpen(false)}>
      <DialogContent className="dark text-white">
        <DialogHeader>
          <DialogTitle>{t.NOTIFICATIONS_DIALOG.LABELS.TITLE}</DialogTitle>
          <DialogDescription>
            {pageType === PageType.APPLIED
              ? t.NOTIFICATIONS_DIALOG.LABELS.DESCRIPTION_APPLIED
              : t.NOTIFICATIONS_DIALOG.LABELS.DESCRIPTION}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-row-reverse items-center gap-6">
          <Button
            className="w-1/2 rounded-full"
            onClick={handleEnableNotifications}
          >
            <BellIcon className="mr-2 h-5 w-5" />
            {t.NOTIFICATIONS_DIALOG.LABELS.ENABLE_BUTTON}
          </Button>
          <Button
            variant="outline"
            className="w-1/2 rounded-full focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            onClick={handleDismiss}
          >
            {dismissButtonText}
          </Button>
        </div>
        {error && <div className="text-red-700 text-sm mt-2">{error}</div>}
      </DialogContent>
    </Dialog>
  );
}
